import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  Input,
  cn,
} from '@design-system';
import { ListFilter, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

interface FilterButtonProps<T> {
  values: T[];
  selectedValues: T[];
  onChange: (values: T[]) => void;
  renderValue?: (value: T) => string;
  getKey?: (value: T) => string;
  searchable?: boolean;
  searchPlaceholder?: string;
}

export function FilterButton<T>({
  values,
  selectedValues,
  onChange,
  renderValue = v => (v == null ? 'Undefined' : String(v)),
  getKey = v => (v == null ? '__none__' : String(v)),
  searchable = false,
  searchPlaceholder = 'Search...',
}: FilterButtonProps<T>) {
  const [search, setSearch] = useState('');
  const selectedKeys = new Set(selectedValues.map(getKey));

  const handleToggle = (value: T) => {
    const key = getKey(value);
    const next = selectedKeys.has(key)
      ? selectedValues.filter(v => getKey(v) !== key)
      : [...selectedValues, value];
    onChange(next);
  };

  const filteredValues = useMemo(() => {
    if (!searchable || !search.trim()) return values;
    const needle = search.trim().toLowerCase();
    return values.filter(v => renderValue(v).toLowerCase().includes(needle));
  }, [values, search, searchable, renderValue]);

  return (
    <DropdownMenu
      onOpenChange={open => {
        if (!open) setSearch('');
      }}
    >
      <DropdownMenuTrigger>
        <button
          className={cn(
            'text-muted-foreground hover:text-foreground inline-flex items-center justify-center rounded p-0.5',
            {
              'text-primary bg-primary/20': selectedKeys.size > 0,
            },
          )}
        >
          <ListFilter className="size-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn('overflow-hidden p-0', searchable && 'w-64')}
      >
        {searchable && (
          <div className="border-b p-2">
            <div className="relative">
              <Search className="text-muted-foreground absolute left-2 top-1/2 size-3.5 -translate-y-1/2" />
              <Input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => {
                  // Prevent Radix menu from hijacking arrow/typeahead keys
                  e.stopPropagation();
                }}
                placeholder={searchPlaceholder}
                className="h-8 pl-7 text-sm"
              />
            </div>
          </div>
        )}
        <div className="max-h-64 overflow-y-auto">
          {filteredValues.length === 0 ? (
            <div className="text-muted-foreground px-3 py-4 text-center text-sm">
              No matches
            </div>
          ) : (
            filteredValues.map(value => (
              <DropdownMenuCheckboxItem
                key={getKey(value)}
                checked={selectedKeys.has(getKey(value))}
                onCheckedChange={() => handleToggle(value)}
                onSelect={e => e.preventDefault()}
              >
                <span className="capitalize">{renderValue(value)}</span>
              </DropdownMenuCheckboxItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
